import { ReactNode, useMemo, useState } from 'react';
import gql from 'graphql-tag';
import { ModalsKeys } from 'global';
import { Stack, Button, Box } from '@mui/material';
import { AddOutlined } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQuery } from '@apollo/client';
import { ModalsContainer } from 'components/shared/Modals';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { CurrencyInputField, InputField, InterestInputField, SelectField } from 'components/form';
import { DeclineForm } from './DeclineForm';

import FileUploadModal from '../../../../pages/LenderPortal/DecisionFileUploadModal';
import { SET_CS_REFETCH } from '../../../../redux/types';
import {
  DeclineReasonOption,
  DecisionCardFormValues,
  CreditSubmissionDeclindeReasonsResponse,
} from 'modules/creditSubmission/types';
import { AddEditDeclineReasonDialog } from '../dialogs';
import { getSubmissionDeclineReasonsOptions } from 'modules/creditSubmission/lib';
import { GET_CREDIT_SUBMISSION_DECLINE_REASONS } from 'modules/creditSubmission/api/getCreditSubmissionDeclineReason';
import MessageModal from '../../../../components/MessageModal';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

interface DecisionCardProps {
  isDisabled?: boolean;
  onDecisionChanged?: (nextValue: { rate: number; decision: number; approvedAmount: number }) => void;
  cs: {
    notes?: string;
    decision?: string;
    approvedRate?: number;
    contingencies?: string[];
    approvedAmount?: number;
    creditSubId: number | string;
    masterCreditSubmissionId?: string | null;
    creditSubmissionDeclineReasonId?: string | null;
  };
}

const POST_CREDIT_DECISION = gql`
  mutation ($entityId: String!, $fieldsObj: LenderCreditDecision) {
    postLenderCreditDecision(entityId: $entityId, fieldsObj: $fieldsObj)
  }
`;

const decisionOptions = [
  { value: 803370002, label: 'Declined' },
  { value: 803370003, label: 'Approved' },
  { value: 803370004, label: 'Pre-Approved' },
  { value: 803370000, label: 'Awaiting Review' },
  { value: 803370001, label: 'Requested Additional Information' },
];

const modalsWithKeys: Partial<Record<ModalsKeys, ReactNode>> = {
  [ModalsKeys.AddEditDeclineReasonDialog]: <AddEditDeclineReasonDialog />,
};

const decisionCardSchema = z.object({
  decision: z.number({
    required_error: 'Decision is required',
  }),
  rate: z.number().min(0, 'Rate must be equal or greater than 0').max(100, 'Rate cannot exceed 100').optional(),
  approvedAmount: z.number().optional(),
  notes: z.string().optional(),
  contingencies: z.string().optional(),
  creditSubmissionDeclineReason: z.any().optional(),
});

export const DecisionCard = ({ cs, onDecisionChanged, isDisabled }: DecisionCardProps) => {
  const dispatch = useDispatch();
  const account = useSelector((state: any) => state.account);
  const refetchCS = useSelector((state: any) => state.cs.refetch);

  const creditId = cs.creditSubId.toString();

  const [openMessageModal, setOpenMessageModal] = useState<boolean>(false);
  const [submissionMessage, setSubmissionMessage] = useState<{ message: string; title: string }>({
    title: '',
    message: '',
  });

  const handleCloseModal = () => {
    setOpenMessageModal(false);
  };

  const handleOpenModal = () => {
    setOpenMessageModal(true);
  };

  const [declineReasonOption, setDeclineReasonOption] = useState<DeclineReasonOption | null>();
  const [showContingencies, setShowContingencies] = useState(false);

  const [postCreditDecision] = useMutation(POST_CREDIT_DECISION);

  const { data: creditSubmissionDeclineReasonsData, refetch: refetchCreditSubmissionDeclineReasons } =
    useQuery<CreditSubmissionDeclindeReasonsResponse>(GET_CREDIT_SUBMISSION_DECLINE_REASONS, {
      variables: { accountId: account.id },
      onCompleted: data => {
        if (cs.creditSubmissionDeclineReasonId) {
          const declineReasonOption =
            getSubmissionDeclineReasonsOptions(data?.creditSubmissionDeclineReasons || []).find(
              csdr => csdr.value === cs.creditSubmissionDeclineReasonId
            ) || null;

          setDeclineReasonOption(declineReasonOption);
          form.setValue('creditSubmissionDeclineReason', declineReasonOption);
        }
      },
    });

  const { submissionDecision, submissionApprovedAmount, submissionRate, submissionNotes, submissionContingencies } =
    useMemo(
      () => ({
        submissionNotes: cs.notes || '',
        submissionRate: cs.approvedRate ?? undefined,
        submissionContingencies: cs.contingencies?.join('\n') ?? '',
        submissionApprovedAmount: cs.approvedAmount ? cs.approvedAmount : undefined,
        submissionDecision: decisionOptions.find(o => o.label === cs.decision)?.value ?? null,
      }),
      [cs]
    );

  const form = useForm<DecisionCardFormValues>({
    disabled: isDisabled,
    mode: 'onChange', // Validates on every change
    resolver: zodResolver(decisionCardSchema),
    defaultValues: {
      rate: submissionRate,
      notes: submissionNotes,
      decision: submissionDecision,
      creditSubmissionDeclineReason: '',
      contingencies: submissionContingencies,
      approvedAmount: submissionApprovedAmount,
    },
  });

  const decision = useWatch({ control: form.control, name: 'decision' });

  const submit = async (formValues: DecisionCardFormValues) => {
    try {
      const payload = {
        decision: formValues.decision,
        approvedAmount: formValues.approvedAmount,
        rate: formValues.rate,
        notes: formValues.notes,
        contingencies: formValues.contingencies ? [formValues.contingencies] : null,
        masterCreditSubmissionId:
          !!cs.masterCreditSubmissionId && cs.masterCreditSubmissionId ? cs.masterCreditSubmissionId : null,
        creditSubmissionDeclineReasonId:
          (formValues.creditSubmissionDeclineReason as DeclineReasonOption)?.value || null,
      };

      handleOpenModal();

      setSubmissionMessage({
        title: 'Sending',
        message: 'Please wait. Submitting your request to lenders...',
      });

      await postCreditDecision({ variables: { entityId: creditId, fieldsObj: payload } });
      setSubmissionMessage({
        title: 'Success',
        message: 'Submitted Successfully',
      });

      dispatch({ type: SET_CS_REFETCH, payload: { refetch: !refetchCS } });
    } catch (err: any) {
      const defaultErrorTitle = 'Error';
      const defaultErrorMessage = 'There was something wrong with your request';
      // Check if it's a GraphQL error with specific error codes
      if (err?.graphQLErrors?.length > 0) {
        const graphQLError = err.graphQLErrors[0];
        const errorCode = graphQLError?.extensions?.code;

        switch (errorCode) {
          case 'BAD_USER_INPUT':
            setSubmissionMessage({
              title: 'Invalid Input',
              message: graphQLError.message || 'Please check your input values and try again.',
            });
            break;

          default:
            setSubmissionMessage({
              title: defaultErrorTitle,
              message: defaultErrorMessage,
            });
            break;
        }
      } else {
        // Handle network errors or other non-GraphQL errors
        setSubmissionMessage({
          title: defaultErrorTitle,
          message: defaultErrorMessage,
        });
      }
    }
  };

  return (
    <CardContainer title="Decision">
      <FormProvider {...form}>
        <SelectField
          name="decision"
          label="Decision"
          control={form.control}
          options={decisionOptions}
          required
          extraAction={newValue => {
            onDecisionChanged?.({
              decision: +newValue,
              rate: submissionRate ?? 0,
              approvedAmount: submissionApprovedAmount ?? 0,
            });

            form.reset({
              decision: +newValue,
              rate: submissionRate,
              notes: submissionNotes,
              contingencies: submissionContingencies,
              approvedAmount: submissionApprovedAmount,
              creditSubmissionDeclineReason: declineReasonOption,
            });
          }}
        />

        {!!decision && (
          <>
            <Stack gap="0.5rem">
              {decision === 803370003 && (
                <>
                  <Stack direction="row" alignItems="baseline" gap="1rem">
                    <CurrencyInputField
                      control={form.control}
                      label="Approved Amount"
                      name="approvedAmount"
                      rules={{ required: true }}
                      textFieldProps={{ required: true }}
                      customOnChange={nextValue => {
                        form.setValue('approvedAmount', nextValue);
                        onDecisionChanged?.({ decision, rate: submissionRate ?? 0, approvedAmount: nextValue });
                      }}
                    />

                    <InterestInputField
                      control={form.control}
                      name="rate"
                      textFieldProps={{
                        label: 'Rate',
                        required: true,
                        inputProps: { max: 100, min: 0 },
                      }}
                      customOnChange={async nextValue => {
                        form.setValue('rate', nextValue);
                        await form.trigger('rate'); // Explicitly trigger validation
                        onDecisionChanged?.({
                          decision,
                          rate: nextValue,
                          approvedAmount: form.getValues('approvedAmount') ?? 0,
                        });
                      }}
                    />
                  </Stack>
                </>
              )}

              {decision !== 803370000 && decision !== 803370002 && (
                <InputField
                  name="notes"
                  label={decision === 803370003 ? 'Notes' : 'Request'}
                  control={form.control}
                  rules={{ required: true }}
                  inputProps={{ multiline: true, fullWidth: true, rows: 5, variant: 'outlined', required: true }}
                />
              )}

              {decision === 803370002 && creditSubmissionDeclineReasonsData?.creditSubmissionDeclineReasons?.length && (
                <DeclineForm<DecisionCardFormValues>
                  isEditable
                  creditSubmissionDeclineReasons={creditSubmissionDeclineReasonsData?.creditSubmissionDeclineReasons!}
                  refetchCreditSubmissionDeclineReasons={refetchCreditSubmissionDeclineReasons}
                />
              )}

              {decision === 803370003 && (
                <>
                  {!showContingencies ? (
                    <Stack direction="row" gap="1rem">
                      <Button
                        size="small"
                        disabled={isDisabled}
                        startIcon={<AddOutlined />}
                        onClick={() => setShowContingencies(true)}
                      >
                        Add Contingencies
                      </Button>

                      <FileUploadModal disabled={isDisabled} cs={cs} />
                    </Stack>
                  ) : (
                    <>
                      <InputField
                        name="contingencies"
                        control={form.control}
                        label="Contingencies (if applicable)"
                        inputProps={{ multiline: true, rows: 7, variant: 'outlined' }}
                      />

                      <Box>
                        <Button onClick={() => setShowContingencies(false)} variant="contained">
                          Cancel
                        </Button>
                      </Box>
                    </>
                  )}
                </>
              )}
            </Stack>

            <Stack direction="row" alignItems="center" gap="1rem" justifyContent="flex-end">
              <Button disabled={isDisabled} onClick={() => setShowContingencies(false)}>
                Cancel
              </Button>
              <Button
                disabled={isDisabled || !!form.formState.errors.rate}
                variant="contained"
                onClick={form.handleSubmit(submit)}
              >
                Submit
              </Button>
            </Stack>
          </>
        )}
      </FormProvider>

      <ModalsContainer modalsWithKeys={modalsWithKeys} />

      <MessageModal
        link={null}
        isOpen={openMessageModal}
        title={submissionMessage.title}
        handleCloseModal={handleCloseModal}
        message={submissionMessage.message}
      />
    </CardContainer>
  );
};
